import { Box, Typography } from '@mui/material';
import { useListContext, useTranslate } from 'react-admin';
import { ProductProgressBar } from '@/components/analytics/product/ProductAnalyticsInfo.jsx';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { styled } from '@mui/material/styles';
import { Growth } from '@/components/analytics/common/Growth.jsx';
import React from 'react';
import PropTypes from 'prop-types';
import { NoDataPlaceholder } from '@/components/analytics/common/NoDataPlaceholder.jsx';
import { analyticsStyles } from '@/themes/commonStyles/analytics.js';
import { formatNumberWithComma } from '@/helpers/format/formatNumber.js';
import { Palette } from '@/themes/themeOverrides/Palette';

const StyledTransactionTimeOfDayAnalytics = styled(Box)(() => ({
    backgroundColor: Palette.white,
    padding: '40px 40px 48px',
    borderRadius: '16px',
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        padding: '24px',
    },
    '& .ranking-header': {
        ...analyticsStyles.rankingHeader,
        marginBottom: '32px',
    },
    '& .ranking-content': {
        ...analyticsStyles.rankingContent,
    },
}));

const StyledElement = styled(Box)(() => ({
    '& + &': {
        marginTop: '32px',
    },
    ...analyticsStyles.element,
}));

const Element = ({ item, total }) => {
    const percentage = (item.current / total) * 100;
    const growth = ((item.current - item.previous) / item.previous) * 100;

    return (
        <StyledElement className='element element--time-of-day'>
            <Box className='element-header'>
                <Box className='element-left'>
                    <Typography variant='caption'>
                        <strong>{item.name}</strong>
                    </Typography>
                </Box>
                <Box className='element-right'>
                    <Typography variant='caption'>{item.current}</Typography>
                    <Growth value={growth} className='growth--small' />
                </Box>
                {isFinite(percentage) ? (
                    <Typography variant='caption' className='percentage'>
                        {formatNumberWithComma(percentage, 1)}%
                    </Typography>
                ) : null}
            </Box>
            <ProductProgressBar percentage={percentage} />
        </StyledElement>
    );
};

export const TransactionTimeOfDayAnalytics = () => {
    const { data } = useListContext();
    const translate = useTranslate();
    if (!data || data.length === 0) return null;

    const analytic = data.find(s => s.id === 'current').data;
    const previous = data.find(s => s.id === 'previous').data;

    const items = ['06-12', '12-18', '18-24', '24-06'].map(key => {
        const [from, to] = key.split('-');
        const dataKey = `transactions${from}${to}Count`;

        return {
            key,
            name: `${from}:00 - ${to}:00`,
            previous: previous[dataKey],
            current: analytic[dataKey],
        };
    });

    return (
        <StyledTransactionTimeOfDayAnalytics>
            <Box className='ranking-header'>
                <Typography variant='h6'>{translate('analytics.transaction.timeOfDay')}</Typography>
            </Box>
            <Box className='ranking-content'>
                {analytic.transactionsCount > 0 ? (
                    items.map(item => <Element key={item.key} item={item} total={analytic.transactionsCount} />)
                ) : (
                    <NoDataPlaceholder />
                )}
            </Box>
        </StyledTransactionTimeOfDayAnalytics>
    );
};

Element.propTypes = {
    item: PropTypes.shape({
        name: PropTypes.string.isRequired,
        previous: PropTypes.number.isRequired,
        current: PropTypes.number.isRequired,
    }).isRequired,
    total: PropTypes.number.isRequired,
};
